import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  applyTheme,
  getStoredPreference,
  resolveTheme,
  storePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme';

interface ThemeContextValue {
  /** The user's stored choice: 'system' (follow device), 'light', or 'dark'. */
  preference: ThemePreference;
  /** The concrete theme currently applied. */
  resolved: ResolvedTheme;
  /** Update the stored choice and re-apply immediately. */
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * ThemeProvider (ADR-013) — drives the active web theme.
 *
 * Default behaviour: follow the device (`prefers-color-scheme`). A resident may
 * override via Account → Appearance; the override persists and wins over the
 * device. When the preference is 'system', live OS theme changes are tracked.
 *
 * The pre-paint boot script in index.html already applied the correct class to
 * avoid a flash; this provider re-syncs from the same source of truth on mount
 * and keeps React state in step with the DOM.
 */
export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredPreference()),
  );

  const setPreference = useCallback((pref: ThemePreference) => {
    storePreference(pref);
    setPreferenceState(pref);
    const next = resolveTheme(pref);
    setResolved(next);
    applyTheme(next);
  }, []);

  // Apply on mount so the provider is self-contained (in production the boot
  // script in index.html already set the class; this re-applies the same value
  // and keeps the DOM coherent in environments without that script, e.g. tests).
  useEffect(() => {
    applyTheme(resolveTheme(preference));
    // Mount-only: subsequent changes are applied by setPreference / the OS listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track live OS theme changes only while following the device.
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      const next = resolveTheme('system');
      setResolved(next);
      applyTheme(next);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
